
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import SearchModal from './SearchModal';

interface NavbarProps {
  transparent?: boolean;
}

const menuItems = [
  { label: 'Productos', to: '/catalogo', icon: 'ri-shirt-line' },
  { label: 'Contacto', to: '/contacto', icon: 'ri-mail-line' },
  { label: '¿Cómo compro?', to: '/como-compro', icon: 'ri-question-line' },
  { label: 'Preguntas Frecuentes', to: '/faq', icon: 'ri-chat-3-line' },
  { label: 'Política de Devolución', to: '/devoluciones', icon: 'ri-arrow-go-back-line' },
  { label: 'Quiénes Somos', to: '/quienes-somos', icon: 'ri-team-line' },
];

export default function Navbar({ transparent = false }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { getCartCount } = useCart();
  const location = useLocation();
  const cartCount = getCartCount();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    if (transparent) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [transparent]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const isTransparent = transparent && !isScrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent ? 'bg-transparent' : 'bg-white shadow-sm'
      }`}
    >
      <div className="w-full px-4 md:px-8 py-3 md:py-4">
        <div className="flex items-center justify-between">

          {/* LEFT: Hamburger menu */}
          <div className="flex items-center gap-3 md:gap-6" ref={menuRef}>
            <div className="relative">
              <button
                onClick={() => setMenuOpen((prev) => !prev)}
                className={`flex flex-col gap-1.5 p-1 cursor-pointer transition-colors ${
                  isTransparent ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                }`}
                aria-label="Abrir menú"
              >
                <span
                  className={`block w-5 md:w-6 h-0.5 transition-all duration-300 ${
                    isTransparent ? 'bg-white' : 'bg-gray-800'
                  } ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}
                />
                <span
                  className={`block w-5 md:w-6 h-0.5 transition-all duration-300 ${
                    isTransparent ? 'bg-white' : 'bg-gray-800'
                  } ${menuOpen ? 'opacity-0' : ''}`}
                />
                <span
                  className={`block w-5 md:w-6 h-0.5 transition-all duration-300 ${
                    isTransparent ? 'bg-white' : 'bg-gray-800'
                  } ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}
                />
              </button>

              {/* Dropdown */}
              {menuOpen && (
                <div className="absolute top-full left-0 mt-3 w-56 md:w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50 animate-fade-in">
                  <div className="py-2">
                    {menuItems.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="flex items-center gap-3 px-4 md:px-5 py-3 md:py-3.5 text-sm text-gray-700 hover:bg-[#F2C4CE]/20 hover:text-gray-900 transition-colors cursor-pointer"
                      >
                        <span className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center text-[#c9909a]">
                          <i className={`${item.icon} text-base`}></i>
                        </span>
                        <span className="font-light tracking-wide">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                  <div className="border-t border-gray-100 px-4 md:px-5 py-3 bg-gray-50">
                    <p className="text-xs text-gray-400 font-light">ninfashion.com</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CENTER: Logo */}
          <Link to="/" className="absolute left-1/2 transform -translate-x-1/2">
            <img
              src="https://public.readdy.ai/ai/img_res/f8ce8ca1-4321-4d57-abbc-1e9093a3a534.png"
              alt="NIN Fashion"
              className="h-8 md:h-12 w-auto"
            />
          </Link>

          {/* RIGHT: Icons */}
          <div className="flex items-center gap-3 md:gap-6">
            <button
              onClick={() => setSearchOpen(true)}
              className={`transition-colors cursor-pointer ${
                isTransparent ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-gray-900'
              }`}
              aria-label="Buscar"
            >
              <i className="ri-search-line text-lg md:text-xl"></i>
            </button>
            <Link
              to="/carrito"
              className={`relative transition-colors ${
                isTransparent ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-gray-900'
              }`}
              aria-label="Carrito"
            >
              <i className="ri-shopping-bag-line text-lg md:text-xl"></i>
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-black text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
            <button
              className="text-xs md:text-sm font-light tracking-wide px-3 md:px-4 py-1.5 md:py-2 rounded-full transition-all whitespace-nowrap cursor-pointer bg-[#F2C4CE] text-gray-800 hover:bg-[#e8adb8]"
              aria-label="Iniciar Sesión"
            >
              <span className="hidden sm:inline">Iniciar Sesión</span>
              <i className="ri-user-line sm:hidden"></i>
            </button>
          </div>
        </div>
      </div>

      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </nav>
  );
}